import { request } from './client';
import type { AuthProvider } from './me';

export type { AuthProvider };

/**
 * Identity entry shape. `email` and `linkedAt` are optional because the
 * current /auth/me response does not include them; they are future- compatible
 * additions that the Settings page can consume when the backend supplies them.
 */
export interface IdentityEntry {
  provider: AuthProvider;
  email?: string | null;
  linkedAt?: string;
  providerTrust?: string;
}

export interface MeResponse {
  userId: string;
  displayName: string | null;
  /**
   * Auth providers linked to this account. Currently a string[]; typed as
   * AuthProvider[] for forward compatibility without requiring backend changes.
   */
  providers: AuthProvider[];
  createdAt: string;
  /** Optional — present when the backend starts returning email on /auth/me. */
  email?: string | null;
  /**
   * Optional — present when the backend returns per-identity detail.
   * Allows the Settings page to show email/linkedAt per provider.
   */
  identities?: IdentityEntry[];
}

export interface WalletChallenge {
  message: string;
  nonce: string;
  expiresAt: string;
}

export interface WalletVerifyResponse {
  userId: string;
  address: string;
}

export interface WalletVerifyPayload {
  address: string;
  message: string;
  nonce: string;
  pubKeyB64: string;
  signatureB64: string;
}

export const authApi = {
  me: () => request<MeResponse>('/auth/me'),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  walletChallenge: () => request<WalletChallenge>('/auth/wallet/challenge', { method: 'POST' }),
  walletVerify: (body: WalletVerifyPayload) =>
    request<WalletVerifyResponse>('/auth/wallet/verify', { method: 'POST', body }),
};
